import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import * as moment from 'moment';
import PropTypes from 'prop-types';
import translate from '../../../../commonComponents/translate';
import Notifications from '../../../../services/notifications';
import withCSRF from '../../../../commonComponents/withCSRF';
import { compose } from '../../../../services/utilityFunctions';
import AmColumnAndLineChart from '../../../../commonComponents/charts/AmColumnAndLineChart';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import CommissionsSalesGraphFilters from './CommissionsSalesGraphFilters';
import WalletProp, { WALLET_TYPE } from '../../proptype/walletProp';

// handle the chart data
const checkDataToUse = (data, isCrypto) => {
  const {
    day: dayParam,
    week: weekParam,
    month: monthParam,
    year: yearParam,
    currencyInfo,
  } = data;

  const formatValue = value => (isCrypto ? value || 0 : (value || 0) / 100);

  let day = [];
  let week = [];
  let month = [];
  let year = [];
  if (dayParam.length > 0) {
    day = dayParam.map(d => ({
      category: `${d.day}/${d.month}`,
      bar1: formatValue(d?.user.amountRevenue), // Personal Sales / Total Turnover (Yellow)
      bar2: formatValue(d?.user.totalCommissions), // Total Commissions (Orange)
      line: formatValue(d?.user.commissionsRevenue), // Personal Commission (Green)
      line2: formatValue(d?.user.referralCommissions), // Referral Commission (Blue)
    }));
  }
  if (weekParam.length > 0) {
    week = weekParam.map(w => ({
      category: `${w.week}-${w.year}`,
      bar1: formatValue(w?.user.amountRevenue), // Personal Sales / Total Turnover (Yellow)
      bar2: formatValue(w?.user.totalCommissions), // Total Commissions (Orange)
      line: formatValue(w?.user.commissionsRevenue), // Personal Commission (Green)
      line2: formatValue(w?.user.referralCommissions), // Referral Commission (Blue)
    }));
  }
  if (monthParam.length > 0) {
    month = monthParam.map(m => ({
      category: `${moment()
        .month(m.month - 1)
        .format('MMM')} '${moment().year(m.year).format('YY')}`,
      bar1: formatValue(m?.user.amountRevenue), // Personal Sales / Total Turnover (Yellow)
      bar2: formatValue(m?.user.totalCommissions), // Total Commissions (Orange)
      line: formatValue(m?.user.commissionsRevenue), // Personal Commission (Green)
      line2: formatValue(m?.user.referralCommissions), // Referral Commission (Blue)
    }));
  }
  if (yearParam.length > 0) {
    year = yearParam.map(y => ({
      category: `${y.year}`,
      bar1: formatValue(y?.user.amountRevenue), // Personal Sales / Total Turnover (Yellow)
      bar2: formatValue(y?.user.totalCommissions), // Total Commissions (Orange)
      line: formatValue(y?.user.commissionsRevenue), // Personal Commission (Green)
      line2: formatValue(y?.user.referralCommissions), // Referral Commission (Blue)
    }));
  }

  return { day, week, month, year, currencySymbol: currencyInfo?.symbol };
};

function CommissionsSalesGraph(props) {
  const { t, _csrf, directSellingSettings, startDate, endDate, activeWallet } =
    props;

  const { loggedUser } = useContext(LoggedUserContext);

  // Graph Filters state (Year, Month, Week and Day)
  const [isActive, setActive] = useState({ filter: 'day' });
  const [dataChart, setDataChart] = useState({});

  const isCrypto = activeWallet && activeWallet.type === WALLET_TYPE.CRYPTO;
  const cryptoCurrency =
    activeWallet?.currencyInfo?.symbol_native ||
    activeWallet?.currencyInfo?.symbol;
  const fiatCurrency =
    activeWallet?.userPriceAvailable?.symbol ||
    dataChart.currencySymbol ||
    activeWallet?.userPriceBalance?.symbol;

  const currency = isCrypto ? cryptoCurrency : fiatCurrency;

  useEffect(() => {
    const params = {
      sponsor: loggedUser._id,
      startDate,
      endDate,
      currency:
        activeWallet?.type === WALLET_TYPE.CRYPTO
          ? activeWallet?.currencyInfo?.code
          : undefined,
    };

    const url =
      activeWallet?.type === WALLET_TYPE.CRYPTO
        ? '/api/analytics/market-place/crypto/commissions/charts'
        : '/api/analytics/market-place/commissions/charts';
    axios
      .get(url, {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        const data = checkDataToUse(response.data, isCrypto);
        setDataChart(data);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchCommissionTotals');
      });
  }, [startDate, endDate, activeWallet]);

  return (
    <div className="commissions-sales-graph-container">
      <div className="commissions-section-header">
        <div className="commissions-section-title">
          {t('commissions.team_sales_revenue')}
        </div>
        <CommissionsSalesGraphFilters
          activeFilter={isActive.filter}
          onFilterChange={filter =>
            setActive(prevState => ({ ...prevState, filter }))
          }
        />
      </div>
      {dataChart?.day?.length > 0 ? (
        <AmColumnAndLineChart
          containerClass="report-chart-content"
          options={{
            category: 'category',
            yLabel: '', // Y TEXT,
            column2ValueKey: 'bar2',
            columnSeriesName: directSellingSettings?.salesIntegration
              ? t('commissions.team_sales_revenue.totalTurnover')
              : t('commissions.team_sales_revenue.personalSales'),
            columnValueKey: 'bar1',
            column2SeriesName: t(
              'commissions.team_sales_revenue.totalCommissions',
            ),
            lineValueKey: 'line',
            lineSeriesName: t(
              'commissions.team_sales_revenue.personalCommission',
            ),
            line2SeriesName: t(
              'commissions.team_sales_revenue.referralCommission',
            ),
            color2Line: '#1DC2FF',
            line2ValueKey: 'line2',
            colorRect2: '#EC6F2D',
            colorRect: '#F5D509',
            colorLine: '#BADC47',
            closerColumns: false,
            unity: currency,
            isCrypto,
          }}
          chartId="teamSalesAndRevenueChart"
          stats={dataChart[isActive.filter]}
          type="layeredColumns"
        />
      ) : (
        <div className="report-chart-content" />
      )}
    </div>
  );
}

CommissionsSalesGraph.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  startDate: PropTypes.number.isRequired,
  endDate: PropTypes.number.isRequired,
  directSellingSettings: PropTypes.shape({
    salesIntegration: PropTypes.bool,
    VAIntegration: PropTypes.bool,
    recurrence: PropTypes.string,
  }).isRequired,
  activeWallet: PropTypes.shape(WalletProp.propType),
};

CommissionsSalesGraph.defaultProps = {
  activeWallet: null,
};

export default compose(translate, withCSRF)(CommissionsSalesGraph);
